export const SYSTEM_STATE_IDS = [
  "SYS-00",
  "SYS-01",
  "SYS-02",
  "SYS-03",
  "SYS-04",
  "SYS-05",
  "SYS-06",
] as const;

export type SystemStateId = (typeof SYSTEM_STATE_IDS)[number];

export interface SystemStateMeta {
  id: SystemStateId;
  slug: string;
  title: string;
  purpose: string;
  /** Deterministic terminal disposition for the state. */
  disposition: string;
}

export const SYSTEM_STATES: SystemStateMeta[] = [
  {
    id: "SYS-00",
    slug: "loading",
    title: "Loading States",
    purpose:
      "Transient surfaces shown while data is being fetched: full-page blocking overlay, shimmer skeletons, and component-level loading.",
    disposition: "Transient — resolves to content when the fetch completes, or to SYS-04 on failure.",
  },
  {
    id: "SYS-01",
    slug: "empty",
    title: "Empty States",
    purpose:
      "Zero-data surfaces across Operations, Compliance and Infrastructure. Positive variants are used when the empty result means everything is healthy.",
    disposition: "Terminal until the underlying record set changes; every empty state offers the next useful action.",
  },
  {
    id: "SYS-02",
    slug: "permission-denied",
    title: "Permission Denied",
    purpose:
      "Shared pattern rendered when a user reaches a resource their role cannot view. It never reveals whether records exist or their count.",
    disposition: "Terminal for the current role — resolves only through an access change, not a retry.",
  },
  {
    id: "SYS-03",
    slug: "stale",
    title: "Stale Source",
    purpose:
      "Degraded data-freshness state. Records stay visible and operations continue on last confirmed values while a provider source is outside its SLA window.",
    disposition: "Recoverable — clears automatically once the provider refresh succeeds.",
  },
  {
    id: "SYS-04",
    slug: "404",
    title: "Network Error / 404",
    purpose:
      "Blocking service error (503) and unknown-route (404) surfaces, plus inline validation, alert banners and toast notifications.",
    disposition: "503 retries on recovery; 404 is terminal and routes back to a known location.",
  },
  {
    id: "SYS-05",
    slug: "maintenance",
    title: "Maintenance / Read-Only",
    purpose:
      "Planned read-only window. All records remain reviewable; mutation controls are intentionally disabled until the window completes.",
    disposition: "Terminal for the window duration — clears when maintenance ends.",
  },
  {
    id: "SYS-06",
    slug: "outcome-unknown",
    title: "OUTCOME_UNKNOWN Danger State",
    purpose:
      "Terminal danger state for transfers whose broadcast outcome could not be determined. Never auto-resolved and never retryable before investigation.",
    disposition: "Terminal — resolved only by manual on-chain verification, never by retry.",
  },
];

export function getSystemState(id: SystemStateId): SystemStateMeta {
  const meta = SYSTEM_STATES.find((s) => s.id === id);
  if (!meta) throw new Error(`Unknown system state id: ${id}`);
  return meta;
}

export function systemStateHref(meta: SystemStateMeta): string {
  return `/system/${meta.slug}`;
}
